import invariant from 'tiny-invariant';
import { useState, useCallback, useMemo } from 'react';

function addPrefix(object, prefix) {
    return Object.fromEntries(Object.entries(object).map(([key, value])=>[
            `${prefix}${key}`,
            value
        ]));
}

function transformFirst(value, transformFn) {
    if (value.length === 0) {
        return "";
    }
    return transformFn(value[0]) + value.slice(1);
}
function toLowerFirst(value) {
    return transformFirst(value, (value)=>value.toLowerCase());
}
function toUpperFirst(value) {
    return transformFirst(value, (value)=>value.toUpperCase());
}

function _extends() {
    _extends = Object.assign || function assign(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source)if (Object.prototype.hasOwnProperty.call(source, key)) target[key] = source[key];
        }
        return target;
    };
    return _extends.apply(this, arguments);
}

function transformDeep(value, mode) {
    if (value === null || value === undefined) {
        return undefined;
    }
    if (Array.isArray(value)) {
        return value.map((val)=>transformDeep(val, mode));
    }
    if (typeof value === "object") {
        const transformKey = mode === "capitalize" ? toUpperFirst : toLowerFirst;
        return Object.entries(value).reduce((accumulator, [key, value])=>_extends({}, accumulator, {
                [transformKey(key)]: transformDeep(value, mode)
            }), {});
    }
    return value;
}
function uncapitalizeDeep(value) {
    return transformDeep(value, "uncapitalize");
}
function capitalizeDeep(value) {
    return transformDeep(value, "capitalize");
}

function assertDefined(value, message) {
    invariant(value !== undefined, message);
}

function assertNotNull(value, message) {
    invariant(value !== null, message);
}

function assertNotEmpty(value, message) {
    invariant(value !== null && value !== undefined, message);
}

function ensureDefined(value, message) {
    assertDefined(value, message);
    return value;
}

function ensureNotNull(value, message) {
    assertNotNull(value, message);
    return value;
}

function ensureNotEmpty(value, message) {
    assertNotEmpty(value, message);
    return value;
}

function downloadFile(dataOrUrl, options = {}) {
    if (typeof dataOrUrl === "string") {
        const { name } = options;
        const a = document.createElement("a");
        a.href = dataOrUrl;
        a.target = "_blank";
        if (name) a.download = name;
        a.click();
    } else {
        const url = URL.createObjectURL(dataOrUrl);
        downloadFile(url, options);
        URL.revokeObjectURL(url);
    }
}

function useRunInTask() {
    const [runningTasks, setRunningTasks] = useState(0);
    const runInTask = useCallback(async (task)=>{
        setRunningTasks((runningTasks)=>runningTasks + 1);
        try {
            return await task();
        } finally{
            setRunningTasks((runningTasks)=>runningTasks - 1);
        }
    }, []);
    return [
        runningTasks > 0,
        runInTask
    ];
}

function useBoundRunInTask(block) {
    const [isRunning, runInTask] = useRunInTask();
    const runBlockInTask = useMemo(()=>block ? (...args)=>runInTask(()=>block(...args)) : undefined, [
        block,
        runInTask
    ]);
    return [
        isRunning,
        runBlockInTask
    ];
}

function useKeyByRoute(routeMatches) {
    const keys = [];
    for(const key in routeMatches){
        const matches = routeMatches[key];
        if (Array.isArray(matches) ? matches.some((match)=>match !== null) : matches !== null) {
            keys.push(key);
        }
    }
    return keys;
}

function useSetUnset(set) {
    return [
        useCallback(()=>set(true), [
            set
        ]),
        useCallback(()=>set(false), [
            set
        ])
    ];
}

function useDialog(onAfterClose) {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [openDialog, closeDialog] = useSetUnset(setIsDialogOpen);
    const close = useCallback(()=>{
        closeDialog();
        if (onAfterClose) setTimeout(onAfterClose);
    }, [
        closeDialog,
        onAfterClose
    ]);
    return {
        isDialogOpen,
        openDialog,
        closeDialog: close
    };
}

export { addPrefix, assertDefined, assertNotEmpty, assertNotNull, capitalizeDeep, downloadFile, ensureDefined, ensureNotEmpty, ensureNotNull, toLowerFirst, toUpperFirst, uncapitalizeDeep, useBoundRunInTask, useDialog, useKeyByRoute, useRunInTask, useSetUnset };
